<script setup lang="ts">
import { usePaymentStore } from "@/components/payment/usePaymentStore";
import { useBooking as useBookingStore } from "@/store/useBooking";
import { useCalendarStore } from "@/store/useCalendarStore";
import { useDiscountCode } from "@/store/useDiscountCode";
import { useGlobalErrorStore } from "@/store/useGlobalError";
import {
  formatPrice, getBookingStatus,
  getTODAffectedPrice,
  Round2Num,
} from "@/utils/Formatting";
import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { useLocationStore } from "@/utils/LocationStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { useUserRole } from "@/utils/useUserRole";
import { emailValidator, requiredValidator } from "@validators";
import { cloneDeep } from "lodash";
import isEmpty from "lodash/isEmpty";
import { useRoute, useRouter } from "vue-router";
import { toast } from "vue3-toastify";
import { useTheme } from "vuetify";
import ActivityStep from "./steps/ActivityStep.vue";
import DateTimeStep from "./steps/DateTimeStep.vue";
import MembersStep from "./steps/MembersStep.vue";
import type { BookingEmit, BookingFormData } from "./types/index";
import { useBooking } from "./useBooking";

import { useUserModeChange } from "@/store/useUserModeChange";
import { storeToRefs } from "pinia";
import {VForm} from "vuetify/components/VForm";
const userMode = useUserModeChange();
const { isUserMode } = storeToRefs(userMode);

const props = withDefaults(
  defineProps<{
    isDrawerOpen: boolean;
    areaList?: any[];
    memberList?: any[];
    locationList?: any[];
    timeZone?: string;
    isQuickBooking?: boolean;
    currentLocation?: string;
    booking?: any;
    initialStep?: number;
    userId?: string;
    couponValue?: number;
    currentLoggedInRoles?: any[];
    membership?: any;
    activityList?: any[];
    location?: any;
    loggedUser?: any;
  }>(),
  {
    timeZone: "Pacific/Auckland",
    currentLoggedInRoles: () => [
      {
        name: "Admin",
        location: "default-location",
      },
    ],
  }
);

// Emit
const emit = defineEmits<BookingEmit>();

const vuetifyTheme = useTheme();

// Store

const userStore = useUserStore();
const locationStore = useLocationStore();

const globalErrorStore = useGlobalErrorStore();
const store = useCalendarStore();
const bookingStore = useBookingStore();
const paymentStore = usePaymentStore();
const discount = useDiscountCode();
const userRole = useUserRole();
const { handleUpdateErrorMessage } = globalErrorStore;
const globalStore = useGlobalSetupStore();
const { currentLocationDetails } = storeToRefs(userStore);
const { globalSetup } = storeToRefs(globalStore);
const { locationList } = storeToRefs(locationStore);

const { globalErrorMessage, isBookingWarningDialogVisible, errorDateList } =
  storeToRefs(globalErrorStore);

const { docketId, docketLineId, isPendingBooking } = storeToRefs(paymentStore);
const { areaLists } = storeToRefs(bookingStore);
const activityList = ref(props.activityList.filter(act => !act?.futureBookingDisabled) || []);
const { discountCode, discountCodeData } = storeToRefs(discount);
const { getCurrentLocation, getUserCreditCards } = userStore;
const docketBackId = ref<string>("");
const referrers = ref<any[]>([]);
const payMethod = ref<any>([]);
const { permissionListMap, userInfo } = storeToRefs(userRole);
const { userCreditCards } = storeToRefs(userStore)

// composable
const booking = useBooking();

const {
  paymentOptions,
  daysOfWeek,
  alternativeAreaList,
  recurringEndDateConfig,
  getDuration,
  handleTicketPrint,
  reserveSlot,
  removeExistingPendingBooking,
  updateBooking,
  refetchBooking,
} = booking;

// Router
// Refs for step components
const route = useRoute();
const dateTimeStepRef = ref();
const membersStepRef = ref();
const activityStepRef = ref();

// Data
const isBookingViewOnly = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const printTicketIsVisible = ref<boolean>(false);
const isYetToPayDues = ref<boolean>(false);
// Set initial step based on whether we show activity selection
const currentStep: any = ref(props.initialStep);

const form = ref<BookingFormData>({
  id: "",
  title: "",
  date: moment().tz(props.timeZone).format("DD-MM-YYYY"),
  activity: activityList.value.length > 0 ? activityList.value[0]?.id : "",
  guests: [],
  members: [],
  area: null,
  time: null,
  duration: 0,
  actualStartTime: null,
  notes: "",
  visitorEmail: "",
  visitorPhone: "",
  owner: props.userId,
  actualDuration: null,
  selectedOptions: [],
  agreement:false
});
const maxPlayers = ref<number>(0);
const maxVisitors = ref<number>(0);
const minPlayers = ref<number>(0);
const minVisitors = ref<number>(0);
const role = ref<string>("Member");

const bookingCreatedDate = ref<any>("");
const pendingBookingId = ref<any>("");
const showPopup = ref<boolean>(false);
const actualEndTime = ref<any>("");
const isDeleteDialogVisible = ref<boolean>(false);
const isCancelDialogVisible = ref<boolean>(false);
const selectedDurationSlot = ref<number>(0);
const isLeftHandedWarningDialogVisible = ref<boolean>(false);
const isLeftHandedBookingErrorVerified = ref<boolean>(false);
let inactivityTimer: ReturnType<typeof setTimeout>;
let inactivity: any;
const isQrCodeVisible = ref<boolean>(false);
const selectedBookingId = ref<string>("");
const isBookingConfirmDialogVisible = ref<boolean>(false);
const docketLineItems = ref<any[]>([]);
const processingPayment = ref<any>({
  isVisible: false,
  message: "",
  type: "",
  spinner: false,
});
const editBooking = ref<any>({});
const email = ref<string>("");
const refVFormReceipt = ref<any>();
const surchargeAmount = ref<number>(0);

// Router
const router = useRouter();
// computed
const bookingSteps = computed(() => [
  ...((activityList.value?.length ?? 0) > 0
    ? [
        {
          id: 1,
          title: "Activity",
          subtitle: "",
          icon: "tabler-activity",
          size: 30,
          isValid: true,
        },
      ]
    : []),
  {
    id: 2,
    title: "Date & Time",
    subtitle: "",
    icon: "tabler-calendar",
    size: 30,
    isValid: true,
  },
  {
    id: 3,
    title: selectedActivity?.value?.maxPlayers > 1 ? "Options" : "Extras",
    subtitle: "",
    icon: selectedActivity?.value?.maxPlayers > 1 ? "tabler-users" : "mdi-page-next-outline",
    size: 30,
    isValid: true,
  },
    {
      id: 4,
      title: "Agreement",
      subtitle: "",
      icon: "mdi-file-document-edit",
      size: 30,
      isValid: true,
    },
  {
    id: 5,
    title: "Payment",
    icon: "tabler-cards",
    size: 30,
    isValid: false,
  },
]);

const getAgreementMessage = computed(
  () => selectedActivity.value?.bookingAgreement
);

// Computed
const haveEditAccess = computed(() => true);

const isEditBooking = computed(() => Boolean(editBooking.value?.id));

const bookingStatus = computed(() => "futureBooking"); // editBooking.value?.status

// Example booking space value, can be 15, 30, 45, or 60
const getBookingSpacing = computed(
  () => selectedActivity.value?.sessionDuration || 60
);

const selectedActivity = computed(() =>
  activityList.value?.find((el: any) => el.id === form.value.activity)
);

const subtotal = computed(() =>
  docketLineItems.value?.reduce(
    (sum: any, item: any) => sum + item.itemPrice * item.qty,
    0
  )
);
const validateAndPrint = async (callback: (email: string) => void) => {
  try {
    const { valid } = await refVFormReceipt.value?.validate();
    if (valid) {
      callback(email.value); // Pass email to the callback if the form is valid
    }
  } catch (e) {
    console.error("An error occurred during validation:", e);
  }
};
const emailReceipt = async (email: string) => {
  if (docketBackId.value) {
    const data = {
      docketID: docketBackId.value,
      email: email,
      requiresEmail: true,
      createdBy: props.userId,
    };

    const response = await pb.collection("docketReceipts").create(data);

    if (response) {
      toast.success("Email Receipt Sent", {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }
};
const getSurchargeAmount = computed(() => {
  const selectedLocation = locationList.value?.find(
    (l: {
      id: string;
      cardSurchargeFixedFee?: number;
      cardSurchargePercentFee?: number;
    }) => l.id === props.location
  );

  // Destructure with default values to ensure they're numbers
  const { cardSurchargeFixedFee = 0, cardSurchargePercentFee = 0 } =
    selectedLocation || {};

  // Calculate surcharge fee (fixed fee + percentage of the checkout total)
  const surchargeFee =
    cardSurchargeFixedFee + subtotal.value * (cardSurchargePercentFee / 100);

  if (surchargeFee == 0 && surchargeAmount.value > 0) {
    return surchargeAmount.value.toFixed(2);
  }
  // Return the final result formatted to two decimal places
  return surchargeFee.toFixed(2);
});
const totalAmount = computed(() => {
  return (
    Number(cloneDeep(subtotal.value)) -
    Number(cloneDeep(getDiscountedAmount.value)) +
    Number(cloneDeep(getSurchargeAmount.value))
  ).toFixed(2);
});

const getDiscountedAmount = computed(() => {
  let totalAmount = 0;
  let lineTotal = 0;

  for (let i = 0; i < docketLineItems.value.length; i++) {
    const item = docketLineItems.value[i];
    totalAmount += item?.qty * item?.itemPrice;
    lineTotal += Number(item.lineTotal);
  }

  return Round2Num(totalAmount - lineTotal, 2);
});

// Modify member list
const getMemberList = computed(() => {
  return [];
});

const getBookingDiscount = computed(() => {
  return Math.max(
    discountCodeData.value?.discountBookings || 0,
    userMembershipDiscounts?.value?.discountFurtherBookings || 0
  )
});

const getProductDiscount = computed(() => {
  return Math.max(
    discountCodeData.value?.discountProduct || 0,
    userMembershipDiscounts?.value?.discountProduct || 0
  )
});

// configure disable condition
// Any of these one required for edit access
// Role other than Admin
const isActionButtonDisabled = computed(() => {
  if (form.value.id) {
    if (haveEditAccess.value) {
      // admin so allow them to edit
      return false;
    } else if (editBooking.value?.users?.includes(props?.userId)) {
      if (bookingStatus.value === "futureBooking") {
        // future booking so allow them to edit
        return false;
      } else {
        return true;
      }
    } else {
      return true;
    }
  } else {
    return false;
  }
});

const isFieldsDisabled = computed(() => {
  if (form.value.id) {
    if (bookingStatus.value !== "futureBooking") return true;

    return false;
  }

  return false;
});

const areaDurationDisable = computed(() => {
  if (form.value.id) {
    if (bookingStatus.value !== "futureBooking") {
      if (haveEditAccess.value) return false;

      return true;
    }

    return false;
  }

  return false;
});

const isInCurrentTimeSlot = computed(() => {
  if (haveCreateAccess.value) return false;

  const currentTime = moment();

  const bookingStart = moment(
    `${form.value?.date} ${form.value?.time}`,
    "DD-MM-YYYY HH:mm"
  );

  // Determine the duration based on activity type
  const activity = activityList.value?.find(
    (activity: any) => activity.id === form.value?.activity
  );

  const bookingDuration = activity?.sessionDuration;

  const bookingEnd = bookingStart
    .clone()
    .add(Number(form.value.duration), "minutes");

  return currentTime.isBetween(bookingStart, bookingEnd);
});

const isOutHourWarningVisible = computed(() => {
  if (haveCreateAccess.value) return false;

  const dayName = form.value.date
    ? moment(form.value.date, "DD-MM-YYYY").format("dddd").toLowerCase()
    : "sunday";

  const filteredTimes = availableTimes.value?.filter((timeSlot: any) => {
    if (!timeSlot[dayName]) return false;

    // Exclude slots where start and finish times are both 0
    if (timeSlot.startHour === 0 && timeSlot.finishHour === 0) return false;

    return true;
  });

  if (!filteredTimes || filteredTimes.length === 0) return true;

  const bookingTime = (() => {
    const [hours = 0, minutes = 0] = form.value?.time?.split(":") ?? [];

    return Number(hours) * 60 + Number(minutes);
  })();

  return !filteredTimes.some((timeSlot: any) => {
    const startTime = timeSlot.startHour * 60 + timeSlot.startMin;
    const endTime = timeSlot.finishHour * 60 + timeSlot.finishMin;

    // Adjust for overnight time slots
    const isOvernight = timeSlot.startHour > timeSlot.finishHour;

    return isOvernight
      ? bookingTime >= startTime || bookingTime < endTime
      : bookingTime >= startTime && bookingTime < endTime;
  });
});

const haveCreateAccess = computed(() => {
  return permissionListMap?.value?.bookings?.includes("Create") ? true : false;
});

const getAreaList = computed(() => {
  if (!form.value.activity) {
    form.value.activity = activityList.value[0].id;
  }
  return props.areaList?.filter((area: any) =>
    area.activities.includes(form.value.activity)
  );
});

const availableTimes = computed(() => {
  const activity = selectedActivity.value?.expand?.availibleTimes || [];
  const userMemberships = [props?.membership?.id];
  return activity.filter((timeSlot: any) => {
    if (timeSlot.memberships && timeSlot.memberships.length > 0) {
      return timeSlot?.memberships?.some((membership: any) =>
        userMemberships.includes(membership)
      );
    }
    return true;
  });
});


const getMaxBookAhead = computed(() => {
  if(pb.authStore.model?.roles?.length > 0 && !isUserMode.value){
    return 52
  }
  return currentLocationDetails.value?.maxBookAheadWeeks || 0
});

const datePickerConfig = computed(() => {
  const disabledDays = ref([...Array(7).keys()]);
  if (availableTimes.value.length) {
    availableTimes.value?.forEach((time: any) => {
      daysOfWeek.forEach((day, index) => {
        if (time[day]) {
          disabledDays.value = disabledDays.value?.filter(
            (d: any) => d !== index
          );
        }
      });
    });
  } else {
    disabledDays.value = [];
  }
  if (role.value === "Admin" || haveCreateAccess.value) {
    return {
      dateFormat: "d-m-Y",
      disable: [
        function (date: any) {
          const today = new Date();

          today.setHours(0, 0, 0, 0); // Reset the time part to just compare dates

          if (bookingStatus.value === "futureBooking" || !form.value.id) {
            // Disable past dates and any dates in the disabledDays array
            return date < today;
          }
        },
      ],
    };
  } else {
    return {
      dateFormat: "d-m-Y",
      disable: [
        function (date: any) {
          const today = new Date();

          today.setHours(0, 0, 0, 0); // Reset the time part to just compare dates

          if (bookingStatus.value === "futureBooking" || !isEditBooking.value) {
            // Disable past dates and any dates in the disabledDays array
            return date < today || disabledDays.value.includes(date.getDay());
          } else {
            // Disable only the specific days in the disabledDays array
            return disabledDays.value.includes(date.getDay());
          }
        },
      ],
    };
  }
});

const isLeftHandWarning = computed(() => {
  // Check if required data is available
  if (!form.value?.area) return false;

  // Find the area object based on the selected area ID
  const selectedArea = props?.areaList?.find(
    (area) => area.id === form.value.area
  );

  // If the selected area cannot accommodate left-handed members
  if (!selectedArea?.canLeftHanded) {
    // Check if any of the selected members is left-handed
    // Return true if a left-handed member is selected
    return props?.memberList?.some(
      (member) => form.value.members.includes(member.id) && member.leftHanded
    );
  }

  // If the selected area can accommodate left-handed members, return false
  return false;
});

const startDate = computed(() => {
  if (form.value?.date && form.value?.time) {
    const formattedDate = moment(form.value?.date, "DD-MM-YYYY");
    const timeParts: any = form.value?.time.split(":");

    formattedDate.hours(timeParts[0]).minutes(timeParts[1]);

    return formattedDate;
  }

  return null;
});

const getBookingAmount = computed(() => {
  // Calculate base amount from activity and duration
  let baseAmount = 0;

  if (form.value?.activity && form.value?.duration) {
    const durationTotal = mapSelectionToMinutes(
      String(form.value?.duration)?.replace(":", ".")
    );

    let numberOfSlots = Math.ceil(durationTotal / getBookingSpacing.value);
    baseAmount =
      numberOfSlots *
      getTODAffectedPrice(selectedActivity?.value, startDate.value);
  } else if (form.value?.activity && !startDate.value) {
    baseAmount = selectedActivity?.value?.sessionFullPrice;
  }

  return baseAmount;
});

// TODO: handle TOD dues
const handleTODDues = () => {
  const totalPaid = editBooking.value?.paid || 0;

  const discountAmount =
    getBookingAmount.value * (getBookingDiscount.value / 100);

  const calculatedValue = getBookingAmount.value - totalPaid - discountAmount;
  if (calculatedValue < 0) console.log("calculatedValue", calculatedValue);
};

const getBookingGap = computed(() => {
  return selectedActivity.value?.bookingGap || 0;
});

const handleReferrerUpdate = async (referrer: any) => {
  let bookingId = form.value?.id || pendingBookingId.value
  try {
    await pb
      .collection("bookings")
      .update(bookingId, {
        referrer: referrer,
      });
  } catch(e){}
  emit("closeDialog", false);
  router.replace({ path: route.path });
};

const showPayment = ref(true)
const handlePaymentSuccess = async () => {
  window.setTimeout(async() => {
  try {
    localStorage.removeItem('qbid')
    let bID = form.value?.id || pendingBookingId.value
    const booking = await pb.collection("bookings").getOne(bID);
    showPayment.value = false
    try {
      bookingProp.value = await pb.collection("bookings").getOne(bID, {expand:'docketLine'});
      if(bookingProp.value.expand.docketLine?.docket) {
        bookingProp.value.docketId = bookingProp.value.expand.docketLine?.docket
      }

    } catch(e){}
    showPayment.value = true
    editBooking.value = booking;
    editBooking.value.selectedOptions = booking?.options;
    showPopup.value = false;
    pendingBookingId.value = null;


    // Remove the pending booking ID form localStorage
    try {
      localStorage.removeItem('pendingBookingId')
    } catch(e){}
    clearPendingTimer();
    clearInactivityTimer();

    if (editBooking.value.due === 0) {
      localStorage.setItem('qbid',editBooking.value?.id)
      // close booking dialog after 2 if got paid completely

      setTimeout(() => {
        if(!isBookingConfirmDialogVisible.value) {
          emit("closeDialog", false);
        }
      }, 2000);
    }
  } catch (e) {}
  },500)
};

const discountCoupon = (id:any) => {
  let canDiscountCoupon = false

  let startMins = 0
  if(form.value?.area && getAreaList.value){
    getAreaList.value.forEach((area:any) => {
      if(area.id == form.value.area){
        startMins += area?.offSetMinutes || 0
      }
    })
  }

  timeList.value.forEach((time:any) => {
    let offsetTime = time.value.split(":")[0] + ":" + (startMins < 9 ? "0" + startMins : startMins)
    if((time.value == id || offsetTime == id) && time.isAfterHours){
      canDiscountCoupon = true
    }
  })
  return canDiscountCoupon
}

const timeList = computed(() => {
  const times = [];
  if (!form.value.date) return [];

  const interval = selectedActivity.value?.bookingSpacing || getBookingSpacing.value;

  const isToday = moment(form.value?.date, "DD-MM-YYYY").isSame(
    moment(),
    "day"
  );

  const filterPastTimes = (hour: any, min: any) => {
    if (!isToday) return true;

    const currentTime = moment();
    const timeToCheck = moment().hour(hour).minute(min);

    return timeToCheck.isAfter(currentTime, "minute");
  };

  const formatTimeTo12Hour = (hour: any, min: any) => {
    return moment(`${hour}:${min}`, "HH:mm").format("hh:mm A");
  };

  const dayName = form.value.date
    ? moment(form.value.date, "DD-MM-YYYY").format("dddd").toLowerCase()
    : "sunday";

  const filteredTimes = availableTimes.value?.filter(
    (timeSlot: any) => timeSlot[dayName]
  );

  const addToSet = (set:any,add:any, fieldName:string) => {
    let found = false
    set.forEach((s:any) => {
      if(s[fieldName] == add[fieldName]){
        if(add['allUsers']){
          s['allUsers'] = add['allUsers']
        }
        found = true
      }
    })
    if(!found){
      set.add(add)
    }
  }

  let startMins = 0
  filteredTimes.forEach((timeSlot:any) => {
    if(timeSlot.startMin > 0 && (timeSlot.startMin < startMins || startMins == 0)){
      startMins = timeSlot.startMin
    }
  })

  if(form.value?.area && getAreaList.value){
    getAreaList.value.forEach((area:any) => {
      if(area.id == form.value.area){
        startMins += area?.offSetMinutes || 0
      }
    })
  }

  const dynamicTimeList = new Set();
  filteredTimes?.forEach((timeSlot: any) => {
    const {startHour, finishHour, allUsers} = timeSlot;

    if (startHour === 0 && finishHour === 0) {
      // Full-day availability
      for (let hour = 0; hour < 24; hour++) {
        for (let min = 0; min < 60; min += interval) {
          if (isToday && !filterPastTimes(hour, min)) continue;

          const formattedHour = hour.toString().padStart(2, "0");
          const formattedMin = min.toString().padStart(2, "0");
          addToSet(dynamicTimeList, {times: `${formattedHour}:${formattedMin}`, allUsers}, 'times')
        }
      }
    } else if (startHour === 0) {
      // Start at midnight and go until `finishHour`
      for (let hour = 0; hour < finishHour; hour++) {
        for (let min = 0; min < 60; min += interval) {
          if (isToday && !filterPastTimes(hour, min)) continue;

          const formattedHour = hour.toString().padStart(2, "0");
          const formattedMin = min.toString().padStart(2, "0");
          addToSet(dynamicTimeList, {times: `${formattedHour}:${formattedMin}`, allUsers}, 'times')
        }
      }
    } else if (finishHour === 0) {
      // Start at `startHour` and go until midnight
      for (let hour = startHour; hour < 24; hour++) {
        for (let min = 0; min < 60; min += interval) {
          if (isToday && !filterPastTimes(hour, min)) continue;

          const formattedHour = hour.toString().padStart(2, "0");
          const formattedMin = min.toString().padStart(2, "0");
          addToSet(dynamicTimeList, {times: `${formattedHour}:${formattedMin}`, allUsers}, 'times')
        }
      }
    } else {
      // Normal range
      for (let hour = startHour; hour < finishHour; hour++) {
        for (let min = 0; min < 60; min += interval) {
          if (isToday && !filterPastTimes(hour, min)) continue;

          const formattedHour = hour.toString().padStart(2, "0");
          const formattedMin = min.toString().padStart(2, "0");
          addToSet(dynamicTimeList, {times: `${formattedHour}:${formattedMin}`, allUsers}, 'times')
        }
      }
    }
  });

  return Array.from(dynamicTimeList)
    .sort((a:any,b:any) => a.times == b.times ? 0 : a.times < b.times ? -1 : 1)
    .map((time: any) => ({
      label: formatTimeTo12Hour(time.times.split(":")[0], time.times.split(":")[1]),
      value: time.times,
      color: vuetifyTheme.name.value,
      isNormalHour: !!time?.allUsers,
      isAfterHours: !!time?.allUsers
    }));
});

/**
 * Computes and returns the global info message for the booking form.
 *
 * @returns {Object} - The global info message object with properties: color, icon, message, paymentOptions.
 */
const globalInfoMessage = computed(() => {
  const totalPaid = editBooking.value?.paid || 0;

  const discountAmount =
    getBookingAmount.value * (getBookingDiscount.value / 100);
  const calculatedValue = parseFloat(getBookingAmount.value - discountAmount).toFixed(2);

  // Calculate the total duration in minutes
  const durationTotal = mapSelectionToMinutes(
    String(form.value.duration)?.replace(":", ".")
  );
  const isDue = editBooking.value?.id
    ? editBooking.value?.due
    : bookingDue.value;

  isYetToPayDues.value = !!isDue;

  const payOptions =
    haveCreateAccess.value || haveEditAccess.value
      ? paymentOptions.value
      : paymentOptions.value.filter(
          (option) => !["pos", "waiveFee"].includes(option.value)
        );
  const minDuration = durationTotal - getBookingGap.value;

  const startTime = moment()
    .startOf("day")
    .add(minDuration, "minutes");

  let timeAfterGapHours = startTime.format("H")
  let timeAfterGapMinutes = startTime.format("mm")
  if(parseInt(timeAfterGapHours) > 0){
    timeAfterGapHours = timeAfterGapHours + (parseInt(timeAfterGapHours) > 1 ? " hours " : " hour ")
  } else {
    timeAfterGapHours = '';
  }

  let newTimeDisplay = timeAfterGapHours + (timeAfterGapMinutes == '00' ? '' : timeAfterGapMinutes + " minutes")

  let couponList = ""
  if(userHasCoupons.value){
    const durationTotal = mapSelectionToMinutes(
      String(form.value?.duration)?.replace(":", ".")
    );
    let numberOfSlots = Math.ceil(durationTotal / getBookingSpacing.value);

    let couponValue = props?.couponValue ? props?.couponValue : selectedActivity.value?.sessionFullPrice / selectedActivity.value?.couponCost;

    if(selectedActivity?.value?.sessionFullPrice && selectedActivity?.value?.couponCost) {
      if(discountCoupon(form.value?.time)){
        couponValue = selectedActivity?.value?.sessionFullPrice / selectedActivity?.value?.couponCost
      } else {
        couponValue = selectedActivity?.value?.sessionFullPrice / (selectedActivity?.value?.couponCostAfterHours ? selectedActivity?.value?.couponCostAfterHours : selectedActivity?.value?.couponCost)
      }
    }

    const discount = getBookingDiscount.value || 0;
    let totalDue = parseFloat(parseFloat(getBookingAmount.value * 1 * (100 - discount))/ 100);
    let couponValueAfterDiscount = parseFloat((parseFloat(couponValue) * 1 * (100 - discount)) / 100);
    totalDue = totalDue - (editBooking?.value?.paid ? parseFloat(editBooking?.value?.paid) : 0)
    let quantity = Math.ceil(totalDue / couponValueAfterDiscount)

    if(quantity > 0) {
      couponList = "(or " + parseInt(quantity) + " coupon" + (parseInt(quantity) > 1 ? "s" : "") + " )"
    }
  }
  const baseMessage: any = {
    color: "dialogHeaderColor",
    icon: "",
    message: `$${calculatedValue} ${couponList} - ${newTimeDisplay} of ${selectedActivity.value?.bookingShortDescription ? selectedActivity.value?.bookingShortDescription : 'Play or Practice'}
     ${selectedActivity?.value?.maxPlayers > 2 ? 'with up to ' + selectedActivity?.value?.maxPlayers + ' Players' : ''
    }`,
    paymentOptions: isDue ? payOptions : undefined,
  };

  const isActivityStep = activityList?.value?.length > 0;
  let cal_currentStep = currentStep.value;
  if (!isActivityStep) {
    cal_currentStep += 1;
  }

  if (cal_currentStep === 0) {
    return {
      ...baseMessage,
      message: "Please select an activity",
    };
  }

  if (cal_currentStep === 2) {
    return {
      ...baseMessage,
      message: `${selectedActivity?.value?.name} ${globalSetup.value.areaPlural} can support up to ${selectedActivity?.value?.maxPlayers} Players each`,
    };
  }

  if (cal_currentStep === 3 && !form.value.agreement) {
    return {
      color:'error',
      message: "You must accept the Terms and Conditions",
    };
  }

  if (cal_currentStep === 4 || cal_currentStep === 3) {
    const statusColorName = isDue
      ? bookingStatus.value === "futureBooking"
        ? "warning"
        : "error"
      : "dialogHeaderColor";

    const statusIcon = isDue
      ? bookingStatus.value === "futureBooking"
        ? "tabler-alert-triangle"
        : "tabler-xbox-x"
      : "";

    return {
      ...baseMessage,
      color: statusColorName,
      icon: statusIcon,
      message: isDue
        ? `The Session fee of $${bookingDue.value} ${couponList} has not been paid yet`
        : editBooking.value?.payMethod?.length > 1
        ? `Booking paid by ${editBooking.value.payMethod.join(", ")}`
        : editBooking.value?.payMethod?.length === 1 &&
          editBooking.value.payMethod[0] === "Fee Waived"
        ? "Booking fee has been waived."
        : "The Session has been fully paid.",
    };
  }

  return baseMessage;
});

const handleMembersError = (error: any) => {
  handleUpdateErrorMessage(error.isVisible, error.message);
};

const handleDateTimeError = (error: any) => {
  handleUpdateErrorMessage(
    error.isVisible,
    error.message,
    "warning",
    "tabler-alert-triangle"
  );
};

// Methods
const validateMemberVisitors = async () => {
  /**
   * Validates the number of members and guests selected in the booking form.
   *
   * @returns {Promise<boolean>} - Returns true if the validation is successful, false otherwise.
   */
  // Calculate the number of selected members
  const selectedMembers = 0;

  // Calculate the number of selected guests
  const selectedGuests = form.value.name ? 1 : 0;

  // Check if the total number of selected members and guests is less than the minimum number of players
  if (selectedMembers + selectedGuests < minPlayers.value) {
    // Display an error message if the condition is true
    const message = `You must select at least ${minPlayers.value} players.`;

    handleUpdateErrorMessage(true, message);

    return false;
  }

  // Check if the total number of selected members and guests is greater than the maximum number of players
  if (selectedMembers + selectedGuests > maxPlayers.value) {
    // Display an error message if the condition is true
    const message = "You have selected too many players.";

    handleUpdateErrorMessage(true, message);

    return false;
  }

  // Clear the error message if the validation is successful
  handleUpdateErrorMessage(false, "");

  return true;
};

const handleGetBooking = async ({ bookingId, payLater }: any) => {
  payLater = currentLocationDetails?.value?.payInStoreEnabled === false ? false : payLater
  const booking = await pb.collection("bookings").getOne(bookingId);
  editBooking.value = booking;
  editBooking.value.selectedOptions = booking?.options;
  if (!props?.isQuickBooking) {
    if (payLater || booking?.due == 0) {
      isBookingConfirmDialogVisible.value = true;
    }
  }

  if (booking?.due == 0 && props?.isQuickBooking && !isBookingConfirmDialogVisible.value) {
    localStorage.setItem('qbid',editBooking.value?.id)
    emit("closeDialog", false);
    router.replace({ path: route.path });
  }
};

const gotToPos = (docketId: string) => {
  /**
   * Function to navigate to the POS list screen.
   *
   * @param {string} docketId - The ID of the docket to navigate to.
   *
   * @emits {closeDialog} - Emits an event to close the booking dialog after navigating to the POS list screen.
   *
   * @returns {void}
   */
  // move user to pos list screen
  router.push({
    name: "pos",
    query: {
      did: docketId,
    },
  });
  setTimeout(() => {
    emit("closeDialog", false);
  }, 1000);
};

const mapSelectionToMinutes = (selectedValue: any) => {
  // Ensure the input is a string
  selectedValue = selectedValue.toString();

  // If the input is a whole number (e.g., "1"), treat it as hours
  if (!selectedValue.includes(".")) return Number(selectedValue) * 60;

  // Split the time string and convert to minutes
  const [hours = "0", minutes = "0"] = selectedValue.split(".").map(Number);

  return hours * 60 + minutes;
};

const selectedProducts = computed(() => {
  return form.value.selectedOptions?.map((optionId) => {
    const option = selectedActivity.value?.expand?.options?.find(
      (opt: any) => opt.id === optionId
    );
    const finalDiscount: any = option.maxDiscount
      ? Math.min(getProductDiscount.value, option.maxDiscount)
      : getProductDiscount.value;
    return {
      id: option?.id,
      name: option?.name,
      sellPrice: option?.sellPrice,
      itemPrice: option?.sellPrice - option?.sellPrice * (finalDiscount / 100),
      qty: 1,
    };
  });
});

const getProductDiscountAmount = computed(() => {
  let totalDiscount = 0;
  // Loop through selected options
  form.value.selectedOptions?.forEach((optionId) => {
    const option = selectedActivity.value?.expand?.options?.find(
      (opt: any) => opt.id === optionId
    );
    if (option) {
      const finalDiscount: any = option.maxDiscount
        ? Math.min(getProductDiscount.value, option.maxDiscount)
        : getProductDiscount.value;
      // Calculate discount amount for this option
      const discountAmount = (option.sellPrice || 0) * (finalDiscount / 100);
      totalDiscount += discountAmount;
    }
  });

  return totalDiscount;
});

const assignRole = () => {
  if (!props?.currentLoggedInRoles) {
    role.value = "";
  }

  // Filter the current logged-in user's roles for the selected location
  let currentLocationRole = props?.currentLoggedInRoles?.filter(
    (role) => role.location === props?.currentLocation
  );

  // If the current logged-in user has roles at the selected location
  if (currentLocationRole?.length) {
    // If the current logged-in user has an admin role at the selected location
    if (currentLocationRole.some((role) => role.name?.includes("Admin"))) {
      // Assign the admin role
      role.value = "Admin";
    } else if (
      // If the current logged-in user has a staff role at the selected location
      currentLocationRole.some((role) => role.name?.includes("Staff"))
    ) {
      // Assign the member role
      role.value = "Member";
    } else {
      // If the current logged-in user has no admin or staff role at the selected location
      // Assign the user role
      role.value = "User";
    }
  } else {
    // If the current logged-in user has no roles at the selected location
    // Assign an empty string as the role
    role.value = "";
  }
};

const productAmount = computed(() => {
  // Add selected options prices
  const optionsAmount =
    form.value?.selectedOptions?.reduce((sum, optionId) => {
      const option = selectedActivity.value?.expand?.options?.find(
        (opt: any) => opt.id === optionId
      );
      return sum + (option?.sellPrice || 0);
    }, 0) || 0;
  return optionsAmount;
});

const bookingDue = computed(() => {
  return (
    getBookingAmount.value +
    productAmount.value -
    (editBooking.value?.paid || 0) -
    getBookingAmount.value * (getBookingDiscount.value / 100) -
    getProductDiscountAmount.value
  ).toFixed(2);
});

/**
 * Handles the submission of the booking form.
 * Validates all forms, updates the booking details, and makes an API call to update the booking.
 *
 * @returns {Promise<void>}
 */
const handleSubmit = async () => {
  // Validate all forms
  const valid = form.value.agreement;
  const durationTotal = mapSelectionToMinutes(
    String(form.value.duration)?.replace(":", ".")
  );

  localStorage.removeItem("pendingBookingId")

  if (!valid) {
    return;
  }

  selectedDurationSlot.value = durationTotal / getBookingSpacing.value;

  const bookingDetails = {
    ...form.value,
    date: form.value?.date,
    due: bookingDue.value,
    value: getBookingAmount.value,
    id: pendingBookingId.value || form.value.id,
    updatedBy: props?.loggedUser?.id || "",
    createdBy: props?.loggedUser?.id || "",
    duration: durationTotal,
    actualStartTime: form.value?.actualStartTime
      ? moment(form.value?.actualStartTime, "DD-MM-YYYY hh:mm a").isValid()
        ? moment(
            form.value?.actualStartTime,
            "DD-MM-YYYY hh:mm a"
          ).toISOString()
        : null
      : null,
    actualDuration: editBooking.value?.actualDuration
      ? form.value?.actualDuration
      : null,
    slot: selectedDurationSlot.value,
    sessionPrice: selectedActivity?.value?.sessionFullPrice,
    recurring: {},
    options: form.value.selectedOptions || [],
  };

  if (props?.loggedUser) {
    bookingDetails.owner = props?.loggedUser?.id;
    bookingDetails.users = props?.loggedUser?.id;
    bookingDetails.members = [props?.loggedUser?.id];
  }
  //showPopup.value = false;
  //pendingBookingId.value = null;

  try {
    isLoading.value = true;
    await updateBooking(bookingDetails);
    //clearPendingTimer();

    if (!errorDateList.value.length) {
      // Goto payment tab
      currentStep.value = bookingSteps?.value?.length - 1;
    }
  } catch (e: any) {
    const { message } = e?.response;
    if (message) {
      handleUpdateErrorMessage(true, message);
    }
  } finally {
    isLoading.value = false;
  }
};

const getReferrers = async () => {
  const referrers = await pb.collection("referrers").getFullList();
  return referrers;
};

const getMaxActivityVisitors = () => {
  /**
   * Updates the maximum number of players and visitors based on the selected activity.
   *
   * @returns {void}
   */
  // Find the selected activity in the activity list
  const activity = activityList?.value?.find(
    (item: any) => item.id === form.value.activity
  );

  // Update the maximum number of players and visitors based on the selected activity
  maxPlayers.value = activity?.maxPlayers;
  maxVisitors.value = activity?.maxVisitors;
  minPlayers.value = activity?.minPlayers;
  minVisitors.value = activity?.minVisitors;
};


const removeConfirmedBooking = async (booking:any) => {
  pendingBookingId.value = form?.value?.id
  if(pendingBookingId.value) {
    try {
      await pb.collection("bookings").update(pendingBookingId.value, {state: 'Pending'});
    } catch(e){}
  }
  await confirmCloseBooking()

  try {
    window.location = window.location.pathname
  } catch(e){}

}
const confirmCloseBooking = async () => {
  await removeExistingPendingBooking(
    pendingBookingId.value || props?.booking?.id
  );

  // Clear the form data
  form.value = {
    id: "",
    title: "",
    date: moment().tz(props.timeZone).format("DD-MM-YYYY"),
    activity: "",
    guests: [],
    members: [],
    area: null,
    time: null,
    duration: 1,
    actualStartTime: null,
    notes: "",
    visitorEmail: "",
    visitorPhone: "",
    owner: props.userId,
    location: "",
    actualDuration: null,
    selectedOptions: [],
    agreement:false
  };

  // Reset the current step to the first step
  currentStep.value = props.initialStep;

  // Close the booking dialog
  showPopup.value = false;

  // Clear the pending booking ID
  pendingBookingId.value = null;

  // Clear the error date list
  errorDateList.value = [];

  // Close the booking warning dialog
  isBookingWarningDialogVisible.value = false;
  discountCode.value = "";
  // Emit the closeDialog event with false as the parameter
  emit("closeDialog", false);
};

const closeDialog = async () => {
  console.log("Close Dialog", currentStep?.value, bookingSteps?.value?.length, pendingBookingId.value &&
    bookingSteps?.value?.length - 1 !== currentStep?.value)
  if (
    pendingBookingId.value &&
    bookingSteps?.value?.length - 1 !== currentStep?.value
  ) {
    // show the confirmation message
    isCancelDialogVisible.value = true;
  } else {
    await confirmCloseBooking();
  }
};

const doFullClose = () => {

  //closeDialog()
}

const closeDialogForPayLater = async () => {
  const bookingId = form.value.id || pendingBookingId.value
  localStorage.removeItem('qbid')
  if(bookingId && (currentLocationDetails.value?.payInStoreEnabled || canEditDateAndTimePage())){
    localStorage.setItem('qbid',bookingId)
    try {
      form.value.state = "Confirmed"
      editBooking.value.state = "Confirmed"
      let getBookingDetails = await pb.collection("bookings").getOne(bookingId)
      if(getBookingDetails && getBookingDetails.state != 'Confirmed') {
        let PersontoEmail = getBookingDetails?.users && getBookingDetails?.users.length > 0 ? getBookingDetails.users[0] : getBookingDetails.owner
        await pb.collection("bookings").update(bookingId, {state: "Confirmed"})
        try {
          let sendQue = {
            sendVia: 'email',
            type: 'Booking',
            status: 'New',
            recordID: bookingId,
            user: PersontoEmail
          }
          await pb.collection("sendQueue").create(sendQue)
        } catch (e) {}
      }
    } catch(e){}
  }

  if (bookingSteps?.value?.length - 1 === currentStep?.value && !props.isQuickBooking) {
    await handleGetBooking({
      bookingId: bookingId,
      payLater: true,
    });
  } else {
    await closeDialog();
  }
};

/**
 * Handles the navigation to the next step in the booking process.
 * Validates the current step's form inputs, updates the current step,
 * and handles the creation or update of bookings.
 *
 * @returns {Promise<void>}
 */
const goToNextStep = async () => {
  let isValid = false;
  const isActivityStep = activityList?.value?.length > 0;
  if (isActivityStep) {
    switch (currentStep.value) {
      case 0: // Activity Step (if shown)
        if (activityList?.value?.length > 0) {
          isValid = await validateActivity();
          break;
        }

      // Fall through if no activity step
      case 1: // DateTime Step
        isValid = await validateDateTime();
        break;
      case 2: // Members Step
        isValid = await validateMembers();
        break;
      case 3: // Agreement Step
        isValid = form.value.agreement
        break;

      default:
        isValid = true;
    }
  } else {
    switch (currentStep.value) {
      case 0: // DateTime Step
        isValid = await validateDateTime();
        break;
      case 1: // Members Step
        isValid = await validateMembers();
        break;
      case 3: // Members Step
        isValid = form.value.agreement
        break;

      default:
        isValid = true;
    }
  }

  if (isValid) {
    if (
      (currentStep.value === 1 && isActivityStep) ||
      (!isActivityStep && currentStep.value === 0)
    ) {
      try {
        // Calculate the booking due amount
        const bookingDue = (
          getBookingAmount.value -
          (editBooking.value?.paid || 0) -
          getBookingAmount.value * (getBookingDiscount.value / 100)
        ).toFixed(2);
        // Make a request to reserve the booking slot

        if(form?.value?.id && !pendingBookingId.value){
          pendingBookingId.value = form?.value?.id
        }

        const response = await reserveSlot(
          form.value?.duration,
          form.value?.date,
          form.value?.time,
          form.value?.activity,
          form.value?.area,
          props?.userId,
          props?.isQuickBooking && props?.userId ? props?.userId : "",
          pendingBookingId.value,
          bookingDue,
          [],
          props?.isQuickBooking ? "Quick User Booking" : "Guest Booking"
        );
        if (response) {
          // Update the pending booking ID
          pendingBookingId.value = response;
          localStorage.setItem("pendingBookingId", response);
          form.value.id = response;

          try {
            bookingProp.value = await pb.collection("bookings").getOne(response, {expand:'docketLine'});

            if(bookingProp.value.expand.docketLine?.docket) {
              bookingProp.value.docketId = bookingProp.value.expand.docketLine?.docket
            }

          } catch(e){}

          // Keep Selected Options
          let bookingHistory = await pb.collection('bookings').getOne(form.value.id)
          if(bookingHistory && bookingHistory?.options){
            form.value.selectedOptions = bookingHistory?.options
          }

          // Move to the next step
          currentStep.value++;
          // Clear the error message
          handleUpdateErrorMessage(false, "");
        }
      } catch (e: any) {
        const { message } = e?.response;
        if (message) {
          handleUpdateErrorMessage(true, message);
        }
      }
    } else {
      currentStep.value++;
    }
  }
};

const resetInactivityTimer = () => {
  clearTimeout(inactivityTimer);
  inactivityTimer = setTimeout(showInactivityPopup, 2 * 60 * 1000); // 2 minutes
};

const showInactivityPopup = () => {
  showPopup.value = true;
};

const continueBooking = () => {
  showPopup.value = false;
  resetInactivityTimer();
};

const startInactivityTimer = () => {
  inactivity = setTimeout(async () => {
    showPopup.value = false;
    await confirmCloseBooking();
  }, 2 * 60 * 1000);
};

const clearInactivityTimer = () => {
  if (inactivity !== null) {
    clearTimeout(inactivity);
    inactivity = null;
  }
};

const clearPendingTimer = () => {
  showPopup.value = false;
  window.removeEventListener("mousemove", resetInactivityTimer);
  window.removeEventListener("keydown", resetInactivityTimer);
  window.removeEventListener("touchstart", resetInactivityTimer);
  window.removeEventListener("click", resetInactivityTimer);
  clearTimeout(inactivityTimer);
};

const getActivities = async () => {
  try {
    const records = await pb.collection("activities").getFullList({
      expand: "availibleTimes,TODPricing,options",
      filter: `location = "${props.location}" && futureBookingDisabled = false`,
    });
    if (records) {
      activityList.value = records;
      if (activityList.value.length <= 1) {
        await nextTick(async () => {
          form.value.activity = activityList.value[0].id;
          currentStep.value++
        });
      }
    }
  } catch (e) {}
};

// Watch booking prop
watch(
  () => editBooking.value,
  (newValue) => {
    if (newValue && !isEmpty(newValue)) {
      // Clone the booking object to avoid direct mutations
      const bookingClone = { ...editBooking.value };

      // Assign values from booking to form fields
      docketId.value = bookingClone.docketId;
      docketLineId.value = bookingClone.docketLine;
      form.value = {
        id: bookingClone.id,
        title: bookingClone.title,
        activity: bookingClone.activity,
        area: bookingClone.area,
        date: moment(bookingClone.startDate).format("DD-MM-YYYY"),
        time: bookingClone.startTime
          ? moment(bookingClone.startTime).format("HH:mm")
          : null,
        members: bookingClone.members || bookingClone.Users,
        notes: bookingClone.notes,
        visitorEmail: bookingClone.visitorEmail,
        visitorPhone: bookingClone.visitorPhone,
        owner: bookingClone.owner || null,
        selectedOptions: bookingClone.selectedOptions || [],
        guests: bookingClone.visitors
          ? bookingClone.visitors
              ?.split(",")
              ?.map((item: string) => item.trim())
          : null,
        location: bookingClone.location,
        actualDuration: bookingClone.actualDuration,
        actualStartTime: bookingClone.actualStartTime,
        duration:
          getBookingSpacing.value === 60
            ? bookingClone.duration / getBookingSpacing.value
            : getDuration(bookingClone.duration, getBookingSpacing.value),
      };

      // Handle "in-progress" status updates
      if (bookingClone.status === "inprogress") {
        form.value.actualStartTime = bookingClone.actualStartTime
          ? moment(bookingClone.actualStartTime).format("DD-MM-YYYY hh:mm a")
          : null;
        form.value.actualDuration = bookingClone.actualDuration || null;

        actualEndTime.value = form.value.actualDuration
          ? moment(form.value.actualStartTime, "DD-MM-YYYY hh:mm a", true)
              .add(form.value.actualDuration, "minutes")
              .format("DD-MM-YYYY hh:mm a")
          : null;
      }

      // Assign booking to pendingBookingId if its state is "Pending"
      if (bookingClone.state === "Pending")
        pendingBookingId.value = bookingClone.id;

      // Assign additional metadata
      bookingCreatedDate.value = bookingClone.created;
      handleTODDues();
    } else {
      // Reset form for new booking creation
      form.value = {
        id: "",
        date: moment().tz(props.timeZone).format("DD-MM-YYYY"),
        duration: 1,
        time: null,
        activity: "",
        title: "",
        actualStartTime: null,
        actualDuration: null,
        guests: [],
        members: [],
        area: null,
        notes: "",
        visitorEmail: "",
        visitorPhone: "",
        owner: props.userId,
        selectedOptions: [],
      };
      currentStep.value = props.initialStep;
      showPopup.value = false;
      pendingBookingId.value = null;
    }
  },
  { immediate: true }
);

/**
 * Watch function to handle changes in form fields related to activity, date,
 * actualDuration, and actualStartTime. It updates the form values, checks for
 * area compatibility, and calculates actualEndTime.
 *
 * @listens {[form.value.activity, form.value.date, form.value.actualDuration, form.value.actualStartTime]}
 *
 * @param {[string, Date, number, string]} [newActivityId, newDate, newDuration, actualStartTime] - The new values of the watched form fields.
 *
 * @emits {form.value.area} - Updates the area value if the new activity is not compatible with the current area.
 * @emits {form.value.time} - Updates the time value if the new date does not have any available time slots.
 * @emits {actualEndTime.value} - Calculates the new actualEndTime value based on the new actualStartTime and actualDuration.
 * @emits {getMaxActivityVisitors()} - Calls the getMaxActivityVisitors function when the new activity changes.
 */
watch(
  () => [
    form.value.activity,
    form.value.date,
    form.value.actualDuration,
    form.value.actualStartTime,
  ],
  ([newActivityId, newDate, newDuration, actualStartTime]) => {
    nextTick(() => {
      // Watch for date changes
      // if (newDate && !timeList.value?.length) {
      //   form.value.time = null;
      // }

      // Watch for actualDuration and actualStartTime changes
      if (newDuration && actualStartTime) {
        actualEndTime.value = moment(
          actualStartTime,
          "DD-MM-YYYY hh:mm a",
          true
        )
          .add(newDuration, "minutes")
          .format("DD-MM-YY hh:mm a");
      }

      // Get maximum activity visitors when activity changes
      if (newActivityId) getMaxActivityVisitors();
    });
  }
);

/**
 * Watch function to handle changes in the globalErrorMessage.value.
 * It clears the error message after 2 seconds if it is visible.
 *
 * @listens {globalErrorMessage.value}
 *
 * @param {Object} newError - The new value of the globalErrorMessage.value.
 * @param {boolean} newError.isVisible - Indicates whether the error message is visible.
 *
 * @emits {handleUpdateErrorMessage(false, "", "", "")} - Calls the function to clear the error message.
 */
watch(
  () => globalErrorMessage.value,
  (newError) => {
    if (newError?.isVisible) {
      // clear after 2 sec
      setTimeout(() => {
        handleUpdateErrorMessage(false, "", "", "");
      }, 2000);
    }
  },
  { immediate: true }
);

/**
 * Watch function to handle changes in the isLeftHandWarning.value.
 * It checks if a left-handed warning should be displayed and updates the error message accordingly.
 *
 * @listens {isLeftHandWarning.value}
 *
 * @param {boolean} newValue - The new value of the isLeftHandWarning.value.
 *
 * @emits {handleUpdateErrorMessage(true, "A member is left handed but this bay cannot do left handed", "warning", "tabler-alert-triangle")}
 * - Calls the function to update the error message when a left-handed warning should be displayed.
 */
watch(
  () => isLeftHandWarning.value,
  (newValue) => {
    // red message is higher priority so check before show warning messages
    if (
      newValue &&
      bookingSteps.value?.length - 1 !== currentStep.value &&
      globalInfoMessage?.value?.color !== "error"
    ) {
      handleUpdateErrorMessage(
        true,
        "A member is left handed but this bay cannot do left handed",
        "warning",
        "tabler-alert-triangle"
      );
    }
  },
  { immediate: true }
);

watch(
  () => props?.isDrawerOpen,
  async (newValue) => {
    if (isEditBooking.value) isBookingViewOnly.value = true;

    if (newValue && editBooking.value?.docketId) {
      // fetch docket data
      const docket = await pb
        .collection("dockets")
        .getOne(editBooking.value?.docketId);

      discountCode.value = docket?.discountCode;
    }
    await getCurrentLocation();
    await getUserCreditCards();
    await getBookingOwner();
  }
);

/**
 * Watch to handle drawer open/close, resetting form data,
 * and managing timers for inactivity.
 *
 * @listens {props.isDrawerOpen} - Listens to the drawer open/close state.
 * @listens {isEditBooking.value} - Listens to the edit booking state.
 * @listens {isPendingBooking.value} - Listens to the pending booking state.
 * @listens {showPopup.value} - Listens to the show popup state.
 * @listens {getAreaList.value} - Listens to the area list state.
 *
 * @emits {currentStep.value} - Emits the current step value.
 * @emits {clearPendingTimer()} - Emits the function to clear the pending timer.
 * @emits {isDeleteDialogVisible.value} - Emits the delete dialog visibility state.
 * @emits {isCancelDialogVisible.value} - Emits the cancel dialog visibility state.
 * @emits {isPendingBooking.value} - Emits the pending booking state.
 * @emits {docketId.value} - Emits the docket ID value.
 * @emits {docketLineId.value} - Emits the docket line ID value.
 * @emits {pendingBookingId.value} - Emits the pending booking ID value.
 * @emits {resetInactivityTimer()} - Emits the function to reset the inactivity timer.
 * @emits {startInactivityTimer()} - Emits the function to start the inactivity timer.
 * @emits {alternativeAreaList.value} - Emits the alternative area list value.
 */
watch(
  () => props?.isDrawerOpen,
  (newValue) => {
    if (!newValue) {
      currentStep.value = props.initialStep;
      clearPendingTimer();
      isDeleteDialogVisible.value = false;
      isCancelDialogVisible.value = false;
      isPendingBooking.value = false;
      docketId.value = "";
      docketLineId.value = "";
      pendingBookingId.value = null;
      isLeftHandedBookingErrorVerified.value = false;
      discountCode.value = "";
      discountCodeData.value = {};
      isBookingViewOnly.value = false;
    }
  },
  { immediate: true }
);

watchEffect(() => {
  if (!isEditBooking.value && isPendingBooking.value) {
    resetInactivityTimer();

    const resetEvents = ["mousemove", "keydown", "touchstart", "click"];

    resetEvents.forEach((event) =>
      window.addEventListener(event, resetInactivityTimer)
    );
  }

  if (!showPopup.value) clearInactivityTimer();
  else startInactivityTimer();

  if (getAreaList.value) {
    alternativeAreaList.value = isLeftHandWarning?.value
      ? getAreaList.value?.filter((el) => el?.canLeftHanded)
      : getAreaList.value || [];
  }

});

// Validation methods
const validateDateTime = async () => {
  return await dateTimeStepRef.value?.validate();
};

const validateMembers = async () => {
  return await membersStepRef.value?.validate();
};

const validateActivity = async () => {
  return await activityStepRef.value?.validate();
};

const userHasCoupons = ref(false)

const doesUserHaveCoupons = async () => {
  userHasCoupons.value = false
  if(pb?.authStore?.model?.id){
    let couponCheck = await pb.collection("coupons").getFullList({filter: 'user="' + pb?.authStore?.model?.id + '" && qtyRemaining > 0 && expiryTime >= @now'})
    if(couponCheck && couponCheck.length >0){
      userHasCoupons.value = true
    }
  }
}

const getCouponValue = () => {
  let couponValue = props?.couponValue || 30
  if(selectedActivity?.value?.sessionFullPrice && selectedActivity?.value?.couponCost) {
    if(discountCoupon(form.value?.time)){
      couponValue = selectedActivity?.value?.sessionFullPrice / selectedActivity?.value?.couponCost
    } else {
      couponValue = selectedActivity?.value?.sessionFullPrice / (selectedActivity?.value?.couponCostAfterHours ? selectedActivity?.value?.couponCostAfterHours : selectedActivity?.value?.couponCost)
    }
  }
  return couponValue;
}

const getCouponCost = () => {
  let couponCost = selectedActivity?.value?.couponCost || 1
  if(selectedActivity?.value?.couponCost) {
    if(discountCoupon(form.value?.time)){
      couponCost = selectedActivity?.value?.couponCost
    } else {
      couponCost = selectedActivity?.value?.couponCostAfterHours ? selectedActivity?.value?.couponCostAfterHours : selectedActivity?.value?.couponCost
    }
  }
  return couponCost;
}

onMounted(async () => {
  await getBookingOwner()
  await doesUserHaveCoupons()
  bookingProp.value = {};
  discountCode.value = "";
  assignRole();
  referrers.value = await getReferrers();
  await getActivities();
  const { dpid, sessionId } = route?.query;
  await getUserCreditCards()

  if (!dpid) return;

  // Define status messages mapping
  const statusMessages: any = {
    Canceled: { type: "cancelled", message: "Payment Cancelled" },
    Declined: { type: "declined", message: "Payment Declined" },
    Waiting: { type: "error", message: "Payment Waiting" },
    Pending: { type: "error", message: "Server not responding" },
    Busy: { type: "error", message: "Terminal is Busy" },
    Offline: { type: "offline", message: "Terminal Offline" },
    Approved: { type: "success", message: "Payment Approved" },
    Error: { type: "error", message: "Payment Gateway Error" },
    GotWebResponse: { type: "info", message: "Waiting for server response" },
  };

  let status = "";
  let payment: any;
  let startTime = Date.now();
  const TIMEOUT = 60000; // 60 seconds timeout
  const POLL_INTERVAL = 2000; // 2 seconds interval

  // Poll until we get a final status or timeout
  while (true) {
    // Check for timeout
    if (Date.now() - startTime > TIMEOUT) {
      processingPayment.value = {
        isVisible: true,
        message: "Payment request timed out",
        type: "error",
        spinner: false,
      };
      break;
    }

    try {
      payment = await pb.collection("docketPayments").getOne(dpid, {
        expand: "docket",
      });
      status = payment.status;
      surchargeAmount.value = payment.surchargeAmount || 0;
      // Update processing payment status
      if (status in statusMessages) {
        const { type, message } = statusMessages[status];
        processingPayment.value = {
          isVisible: true,
          message,
          type,
          spinner: ["Pending", "GotWebResponse", "Waiting"].includes(status),
        };
      }

      // Handle terminal redirect
      if (status === "Waiting" && !sessionId && payment.webRedirect) {
        window.location.href = payment.webRedirect;
        break;
      }

      // Handle final statuses
      if (
        [
          "Approved",
          "Canceled",
          "Declined",
          "Error",
          "Timeout",
          "Offline",
          "Busy",
        ].includes(status)
      ) {
        break;
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
    } catch (error) {
      console.error("Error polling payment status:", error);
      processingPayment.value = {
        isVisible: true,
        message: "Error checking payment status",
        type: "error",
        spinner: false,
      };
      break;
    }
  }
  const { docket, amount } = payment;
  const response = await pb.collection("docketLines").getFullList({
    filter: `docket = '${docket}'`,
    expand: "booking",
  });
  docketBackId.value = docket;
  docketLineItems.value = response;
  processingPayment.value = {
    isVisible: true,
    message: `Payment ${status}`,
    type: status === "Approved" ? "success" : "error",
    spinner: false,
  };

  if (status === "Approved") {

    try {
      const docketRecord = await pb.collection("dockets").getOne(docket);

      const {expand} = await pb
        .collection("docketLines")
        .getFirstListItem(`docket = '${docket}' && transactionType = 'Booking Payment'`, {
          expand: "booking"
        });

      editBooking.value = expand?.booking;
      const bookingStatus = getBookingStatus(
        editBooking.value?.actualStartTime,
        editBooking.value?.actualDuration
      );
      editBooking.value.status = bookingStatus;
      editBooking.value.docketId = docket;
      editBooking.value.selectedOptions = expand?.booking?.options;
      docketId.value = docket;
      editBooking.value.startDate = editBooking.value?.startTime;
      bookingProp.value = editBooking.value;
      email.value = expand?.booking?.visitorEmail || "";
      if (amount >= docketRecord?.due) {

        await pb.collection("dockets").update(docket, {
          status: "Paid",
          due: 0,
        });

        const booking = await pb
          .collection("bookings")
          .update(`${expand?.booking?.id}`, {
            paid: amount ?? 0,
            due: expand?.booking?.value - amount,
          });

        if(booking && booking.state != 'Confirmed') {
          await pb.collection("bookings").update(`${expand?.booking?.id}`, {state: 'Confirmed'});
          try {
            let PersontoEmail = booking?.users && booking?.users.length > 0 ? booking.users[0] : booking.owner
            let sendQue = {
              sendVia: 'email',
              type: 'Booking',
              status: 'New',
              recordID: expand?.booking?.id,
              user: PersontoEmail
            }
            await pb.collection("sendQueue").create(sendQue)
          } catch (e) {console.log("ERROR", e)}
        }
      }
      currentStep.value = 4;
    } catch (error) {
      console.error("Error updating docket or booking:", error);
    }

  } else {
    currentStep.value = 4;
    const bookingLine = response?.find((el: any) => el?.booking);

    if(!pb.authStore?.model?.id && bookingLine?.booking && bookingLine?.expand?.booking.state != "Confirmed"){
      await pb.collection("bookings").update(bookingLine?.booking, {state:"Pending"})
    }
  }

  // New - get the booking
  if(dpid) {
    try {
      let paymentDocketCheck = await pb.collection("docketPayments").getOne(dpid);
      let DocketLinesCheck = await pb.collection("docketLines").getFirstListItem('docket="' + paymentDocketCheck.docket + '" && booking != ""');
      let bookingDocketCheck = await pb.collection("bookings").getOne(`${DocketLinesCheck.booking}`)
      localStorage.setItem('qbid',DocketLinesCheck.booking)
      editBooking.value = bookingDocketCheck;
      editBooking.value.selectedOptions = bookingDocketCheck.options;
      editBooking.value.docketId = paymentDocketCheck.docket;
      editBooking.value.startDate = editBooking.value?.startTime;

      bookingProp.value = editBooking.value;

      discountCodeData.value = {
        discountBookings: payment?.expand?.docket?.discountBookings,
        discountProduct: payment?.expand?.docket?.discountProducts,
      };
    } catch(e){}
  }
  await getUserCreditCards()

});

// Get Discounts using Owner of the Booking
const userMembershipDiscounts = ref({})
const getBookingOwner = async () => {
  let owner = form.value?.owner ? form?.value?.owner : props?.booking?.owner
  if(owner){
    let userMembershipData = await pb.collection("users").getOne(owner, {expand:'membership'})
    if(userMembershipData?.expand?.membership && !userMembershipData?.preventBenefits){
      userMembershipDiscounts.value = userMembershipData?.expand?.membership
    } else {
      userMembershipDiscounts.value = {}
    }
  } else {
    userMembershipDiscounts.value = {}
  }
}

const bookingProp:any = ref({})

const newValidationRules = () => {
  if(currentLocationDetails.value?.payInStoreEnabled || pb?.authStore?.model?.id){
    return true
  } else {
    if(currentLocationDetails.value?.guestValidateEmail && LocalEmailValidator(form.value.visitorEmail) != true){
      return false
    }
    if(currentLocationDetails.value?.guestValidateSMS && LocalMobileValidator(form.value.visitorPhone) != true){
      return false
    }
  }
  return true
}

const LocalEmailValidator = (value: unknown) => {
  if (isEmpty(value)) return currentLocationDetails.value?.guestValidateEmail ? 'You must provide a valid email' : true;
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  if (Array.isArray(value))
    return (
      value.every((val) => re.test(String(val))) ||
      "The Email field must be a valid email"
    );
  return re.test(String(value)) || "The Email field must be a valid email";
};

const LocalMobileValidator = (value: unknown) => {
  if (isEmpty(value)) return currentLocationDetails.value?.guestValidateSMS ? 'You must provide a valid Mobile Number' : true;

  value = value.replace(/\D/g,'');
  const re = /^([0|\+[0-9]{1,5})?([0-9]{10})$/;
  if (Array.isArray(value))
    return (
      value.every((val) => re.test(String(val))) ||
      "This must be a valid mobile number"
    );
  return re.test(String(value)) || "This must be a valid mobile number";
};

const getAvailableHoursForDay = (timeRanges: any, day: any) => {
  const timeRange = timeRanges?.filter((el: any) => el[day] === true);
  return timeRange?.reduce((acc: number[], el: any) => {
    const { startHour, finishHour } = el;
    if (finishHour === 0) {
      return [
        ...acc,
        ...Array.from({ length: 24 - startHour }, (_, i) => startHour + i), // From startHour to 23
        ...Array.from({ length: finishHour + 1 }, (_, i) => i), // From 0 to finishHour
      ];
    }

    return [
      ...acc,
      ...Array.from(
        { length: finishHour - startHour + 1 },
        (_, i) => startHour + i,
      ),
    ];
  }, []);
};

const canPayInStore = () => {
  if(selectedActivity?.value?.expand?.availibleTimes && startDate.value) {
    const timeRanges = selectedActivity?.value?.expand?.availibleTimes;
    const dayOfWeek = moment(startDate.value, "DD-MM-YYYY")
      .format("dddd")
      .toLowerCase();

    const startTime = moment(startDate.value, "HH:mm");

    const availableHoursForDay = getAvailableHoursForDay(timeRanges.filter((tr: any) => tr.memberships.length == 0), dayOfWeek)

    // remove Last Time Slot
    if(availableHoursForDay.length > 1) {
      availableHoursForDay.pop()
    }
    // Check if the start and end time are within valid hours
    const isValidTimeForCurrentDay = availableHoursForDay.includes(startTime.hour())
    return (currentLocationDetails?.value?.payInStoreEnabled && isValidTimeForCurrentDay) || canEditDateAndTimePage()
  } else {
    return currentLocationDetails?.value?.payInStoreEnabled || canEditDateAndTimePage()
  }
}

const canEditDateAndTimePage = () => {
  return pb.authStore.model?.roles && pb.authStore.model?.roles.length > 0
}
</script>

<template>
  <VDialog
    :model-value="props.isDrawerOpen"
    temporary
    persistent
    no-click-animation
    location="end"
    width="900"
    class="scrollable-content booking_stepper"
  >
    <VOverlay
      v-model="isLoading"
      contained
      persistent
      class="align-center justify-center"
    >
      <VProgressCircular indeterminate />
    </VOverlay>

    <!-- Timeout error dialog -->
    <TimeOutError
      :is-visible="showPopup"
      @closeDialog="closeDialog"
      @continueBooking="continueBooking"
    />

    <!-- leftHandedWarning dialog -->
    <WarningDialog
      :is-visible="isLeftHandedWarningDialogVisible"
      @closeDialog="
        ($event: any) => {
          isLeftHandedWarningDialogVisible = $event;
        }
      "
      @verifiedByUser="
        () => {
          isLeftHandedBookingErrorVerified = true;
          isLeftHandedWarningDialogVisible = false;
        }
      "
    />
    <VCard height="auto" flat>
      <div
        class="d-flex align-center justify-space-between bg-dialogHeaderColor">
        <!-- 👉 Header -->
        <VCardTitle> Add <span v-if="form?.activity && activityList && activityList.length > 0">{{activityList.filter(a => a.id == form.activity).pop().name}}</span> Booking </VCardTitle>

        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          :disabled="currentStep === bookingSteps.length - 1"
          @click="closeDialog"
        />
      </div>

      <!-- Stepper Edit Content  -->
      <VCardText>
        <VRow>
          <!-- large screen stepper -->
          <VCol cols="12" class="stepper-form-header">
            <CustomStepper
              v-model:current-step="currentStep"
              :items="bookingSteps"
              :is-active-step-valid="false"
              class="stepper-icon-step-bg"
              direction="horizontal"
            />
          </VCol>
          <VCol cols="12" class="pt-0">
            <VWindow v-model="currentStep" class="disable-tab-transition" :touch="false" disabled>
              <!-- Activity Step -->
              <VWindowItem v-if="activityList?.length > 0">
                <ActivityStep
                  ref="activityStepRef"
                  v-model:form="form"
                  :activity-list="activityList"
                  :is-fields-disabled="isFieldsDisabled"
                  :is-edit-booking="isEditBooking"
                  :have-create-access="haveCreateAccess"
                  :have-edit-access="haveEditAccess"
                  @validate="validateActivity"
                  @next-step="currentStep++"
                />
              </VWindowItem>

              <!-- DateTime Step -->
              <VWindowItem>
                <DateTimeStep v-if="currentStep == 1"
                  ref="dateTimeStepRef"
                  v-model:form="form"
                  :role-list="props?.currentLoggedInRoles"
                  :activity-list="activityList"
                  :get-area-list="getAreaList"
                  :member-list="getMemberList"
                  :membership="props?.membership"
                  :is-quick-booking="props?.isQuickBooking"
                  :role="role"
                  :date-picker-config="recurringEndDateConfig(getMaxBookAhead)"
                  :is-fields-disabled="isFieldsDisabled"
                  :area-duration-disable="areaDurationDisable"
                  :is-edit-booking="isEditBooking"
                  :have-create-access="haveCreateAccess"
                  :have-edit-access="haveEditAccess"
                  @validate="validateDateTime"
                  @updateErrorMessage="handleDateTimeError"
                />
              </VWindowItem>

              <!-- Members Step -->
              <VWindowItem>
                <MembersStep v-if="currentStep == 2"
                  ref="membersStepRef"
                  v-model:form="form"
                  :is-quick-booking="props?.isQuickBooking"
                  :max-players="maxPlayers || 0"
                  :is-edit-booking="isEditBooking"
                  :member-list="memberList"
                  :is-fields-disabled="isFieldsDisabled"
                  :area-duration-disable="areaDurationDisable"
                  :have-create-access="haveCreateAccess"
                  :have-edit-access="haveEditAccess"
                  :activityList="activityList"
                  :logged-user="props?.loggedUser"
                  @validate="validateMembers"
                  @updateErrorMessage="handleMembersError"
                />
              </VWindowItem>
              <VWindowItem>
                    <div class="booking_stepper__explanation mb-6">
                      Review and agree to the terms and conditions
                    </div>
                    <VCheckbox v-model="form.agreement" :rules="[form.agreement || 'You must accept the terms and conditions']">
                      <template #label>
                        <div class="booking_stepper__agreement-message">
                          {{ getAgreementMessage }}
                        </div>
                      </template>
                    </VCheckbox>
              </VWindowItem>
              <VWindowItem>
                <PaymentForm v-if="currentStep == bookingSteps.length -1 && showPayment"
                  :have-create-permission="haveCreateAccess"
                  :total-due="bookingDue"
                  :products="selectedProducts"
                  :booking-id="form?.id"
                  :bookingProp="bookingProp"
                  :logged-in-user-id="props?.loggedUser.id"
                  :membership="userInfo?.expand?.membership"
                  :total-paid="editBooking?.paid || bookingProp?.paid || 0"
                  :coupon-value="getCouponValue()"
                  :coupon-cost="getCouponCost()"
                  :is-quick-booking="props?.isQuickBooking"
                  :location="selectedActivity?.location"
                  :discountProduct="userInfo?.expand?.membership?.discountProduct || 0"
                  :user-credit-cards="userCreditCards"
                  :discountBookings="userInfo?.expand?.membership?.discountFurtherBookings || 0"
                  :discountEvents="userInfo?.expand?.membership?.discountEvents || 0"
                  :discountSources="[userInfo?.expand?.membership ? 'Membership' : '']"
                  :owner="props?.isQuickBooking ? userInfo.id : form?.owner"
                  :booking-amount="getBookingAmount"
                  :payMethods="editBooking.value?.payMethod"
                  :type="'Booking'"
                  :canPayInStore="canPayInStore()"
                  :source="
                    props?.isQuickBooking
                      ? 'Quick User Booking'
                      : 'Guest Booking'
                  "
                  @paymentSuccess="handlePaymentSuccess"
                  @bookingChange="handleGetBooking"
                  @updateMessage="
                    handleUpdateErrorMessage(
                      $event?.isVisible,
                      $event?.message,
                      $event?.color,
                      $event?.icon
                    )
                  "
                  @goToPosPayment="gotToPos"
                  @showConfirmDialog="isBookingConfirmDialogVisible = true"
                  @addNewCardDialog="emit('addNewCardDialog', true)"
                  @refetch="refetchBooking"
                  @closeDialog="doFullClose"
                  @closeBooking="closeDialog"
                />
              </VWindowItem>
            </VWindow>
          </VCol>
        </VRow>
      </VCardText>

      <!-- Stepper Edit Content Ends -->
      <div class="bold_tag">
        <!-- <div class="d-flex flex-column gap-2 pb-4">
          <div v-if="isInCurrentTimeSlot" class="pl-5">
            Some Details can only be changed by staff as booking in current time
            slot
          </div>
          <div v-if="isOutHourWarningVisible" class="pl-5">
            Some details can only be changed by staff as booked for out of hours
          </div>
        </div> -->
        <VAlert
          v-if="globalErrorMessage?.isVisible"
          :color="globalErrorMessage?.color || 'error'"
          :icon="globalErrorMessage?.icon || 'tabler-xbox-x'"
        >
          {{ globalErrorMessage?.message }}
        </VAlert>
        <VAlert
          v-else
          :color="globalInfoMessage.color"
          :icon="globalInfoMessage?.icon || 'mdi-info'"
        >
          {{globalInfoMessage?.message.split(")")[0]}}
          <span>
             <v-tooltip activator="parent">
                The amount of Issued Membership coupons required to fully cover the cost of the booking
            </v-tooltip>
            <v-icon v-if="globalInfoMessage?.message.indexOf(')') != -1" icon="mdi-info"></v-icon>
          </span>
          <span v-if="globalInfoMessage?.message.indexOf(')') != -1">) {{globalInfoMessage?.message.split(")")[1]}}</span>
        </VAlert>
      </div>
      <VCardText class="button_container">
        <div class="d-flex button_group">
          <div class="d-flex button_group__first_btn">
            <VBtn
              :disabled="currentStep === initialStep"
              color="secondary"
              variant="flat"
              @click.prevent="currentStep--"
            >
              <VIcon icon="mdi-arrow-left" start class="flip-in-rtl" />
              Previous
            </VBtn>

            <!-- QR -->
            <div
              v-if="props?.booking?.id"
              class="booking_dynamic_buttons__large"
            >
              <IconBtn
                color="grey"
                icon="mdi-qrcode"
                class="rounded-sm ml-2 border-md"
                border="secondary lg"
                @click.stop="
                  () => {
                    selectedBookingId = props?.booking?.id;
                    isQrCodeVisible = true;
                  }
                "
              />
            </div>
          </div>
          <div class="d-flex">
            <!-- for large screen alignment button in between  -->
            <div class="booking_dynamic_buttons__large" />
              <VBtn color="error" class="mt-2 mt-xs-0 mt-sm-0" @click="isCancelDialogVisible = true" v-if="currentStep === bookingSteps.length - 1">
                Delete
              </VBtn>
            <VBtn
              v-if="canPayInStore()"
              variant="flat"
              color="secondary"
              class="ml-2 booking_dynamic_buttons__large"
              @click="closeDialogForPayLater"
            >
              {{
                currentStep === bookingSteps.length - 1
                  ? bookingProp?.due === 0 ? "PAID" : "Confirm - Pay Later"
                  : "Close"
              }}
            </VBtn>

            <!-- create booking -->
            <div
              class="align-content-center"
              v-if="bookingSteps.length - 1 !== currentStep"
            >
              <VBtn
                v-if="bookingSteps.length - 2 > currentStep"
                class="ml-2"
                @click="goToNextStep"
              >
                Next
                <VIcon icon="mdi-arrow-right" end class="flip-in-rtl" />
              </VBtn>
              <VBtn
                v-else
                :disabled="isActionButtonDisabled || !newValidationRules()"
                color="success"
                class="ml-2"
                @click="handleSubmit">
                Confirm
              </VBtn>
            </div>
          </div>
        </div>
      </VCardText>
    </VCard>

    <!-- Booking cancel confirmation dialog -->
    <BookingCancelConfirmDialog
      :is-open="isCancelDialogVisible"
      :activity="
        activityList?.find(
          (activity: any) => activity.id === props?.booking?.activity,
        )
      "
      @closeDialog="isCancelDialogVisible = $event"
      @cancelBooking="removeConfirmedBooking(props)"
    />

    <!-- Error date list dialog -->
    <ErrorWarnDatesDialog
      :is-open="isBookingWarningDialogVisible"
      :dates="errorDateList"
      @closeDialog="isBookingWarningDialogVisible = false"
    />
  </VDialog>

  <TicketPrint
    :is-visible="printTicketIsVisible"
    :emailed="props?.booking?.emailed"
    :printed="props?.booking?.printed"
    :have-edit-access="haveEditAccess"
    @print="
      async ($event: any) => {
        await handleTicketPrint($event, props?.booking?.id);
        emit('refetchBookingData', props?.booking?.id);
        printTicketIsVisible = false;
      }
    "
    @closeDialog="printTicketIsVisible = false"
  />

  <QrCode
    :is-visible="isQrCodeVisible"
    :qr-value="selectedBookingId"
    @closeDialog="isQrCodeVisible = false"
  />

  <VDialog
    v-model="processingPayment.isVisible"
    persistent
    no-click-animation
    class="pair_terminal_dialog">
    <VCard width="600">
      <div class="payment-header text-center">
        <!-- Main Payment Amount -->
        <span class="payment-amount text-h4" v-if="totalAmount > 1">
          Payment of {{ formatPrice(totalAmount) }}
        </span><span v-else class="payment-amount text-h4">Processing Payment</span>

        <!-- Payment Method Text -->
        <span class="payment-method">with Card</span>

        <!-- Surcharge Info with Background -->
        <span v-if="getSurchargeAmount" class="surcharge-info">
          ( including surcharge of {{ formatPrice(getSurchargeAmount) }} )
        </span>
      </div>

      <VCardText v-if="processingPayment.spinner" class="text-center">
        <VProgressCircular indeterminate size="64" width="8" />
      </VCardText>

      <!-- Payment Status Messages -->
      <VCardText class="text-center">
        <span
          v-if="processingPayment?.message"
          class="text-h4 rounded pa-2"
          :class="{
            'text-success': processingPayment?.type === 'success',
            'text-error': processingPayment?.type !== 'success',
          }"
        >
          {{ processingPayment?.message }}
        </span>

        <span
          v-if="
            processingPayment?.type === 'declined' &&
            processingPayment?.description
          "
          class="text-h3 rounded pa-2 text-error"
        >
          {{ processingPayment?.description }}
        </span>

        <!-- Success: Email and Print Receipt Options -->
        <VResponsive
          v-if="processingPayment?.type === 'success'"
          class="mx-auto mt-4"
          max-width="344"
        >
          <VForm ref="refVFormReceipt" class="d-flex">
            <VTextField
              v-model="email"
              :rules="[requiredValidator, emailValidator]"
              label="Email Address"
              class="mt-2"
            />
          </VForm>
          <div class="d-flex justify-center flex-wrap gap-4 mt-2">
            <VBtn color="primary" @click="() => validateAndPrint(emailReceipt)">
              Email Receipt
            </VBtn>
          </div>
        </VResponsive>
      </VCardText>

      <VCardText class="d-flex justify-end flex-wrap gap-4">
        <VBtn
          color="error"
          @click="
            async () => {
              processingPayment.isVisible = false;
              if (processingPayment?.type === 'success') {
                handleGetBooking({
                  bookingId: editBooking.id,
                  payLater: true,
                });
              }
            }
          ">
          Close
        </VBtn>
      </VCardText>
    </VCard>
  </VDialog>
  <BookingConfirmDialog
    v-model="isBookingConfirmDialogVisible"
    :booking="editBooking"
    :activities="selectedActivity"
    :referrers="referrers"
    @closeDialog="handleReferrerUpdate"
  />
</template>

<style scoped lang="scss">
.v-slide-group__content {
  padding: 0.625rem;
}

.stepper-icon-step {
  margin-block-start: 0.625rem;
}

.stepper-icon {
  border-radius: 0.3125rem;
  background-color: rgb(var(--v-theme-primary));
  color: rgba(var(--v-theme-on-primary));
}

.stepper-form-header {
  padding: 1.25rem;
  font-size: 0.625rem;
}

.member_list_selection {
  .v-field__input {
    max-block-size: 7.5rem;
    overflow-y: auto;
    scrollbar-width: thin;
  }
}

.button_group {
  display: flex;
  justify-content: space-between;

  &__multi_btn {
    padding-inline-start: 0.625rem !important;
  }
}

.booking_dynamic_buttons {
  &__large {
    display: flex;
  }

  &__small {
    display: none !important;
  }
}

@media (max-width: 720px) {
  .button_group {
    &__first_btn {
      padding: 8px;
    }
  }

  .booking_dynamic_buttons {
    &__large {
      display: none;
    }

    &__small {
      display: flex !important;
    }
  }
}
</style>
